import ShareIcon from '@ixigo/icons/Share';
import Typography from '@ixigo/iui/Typography';
import { Skeleton } from '@repo/shared/components';
import type { BookingStatus, BookingSuccessResponse } from '@/api';
import CopyToClipboard from '@/components/copy-clipboard';
import { NavigationHeader } from '@/components/NavigationHeader';
import { useNavigate } from '@/lib/navigation';
import { TRIP_DETAIL_STYLE_MAP } from '@/routes/_auth/-components/constants';
import { BookingDetailContent } from './booking-detail';
import { RescheduleRefundAlert } from './reschedule-refund-alert';
import { TripWidget } from './trip-widget';

interface BookingHeaderProps {
	bookingId: string;
	bookingStatus?: BookingStatus;
	loading?: boolean;
}

export function BookingHeader({
	bookingId,
	bookingStatus,
	loading = false,
}: BookingHeaderProps) {
	const statusStyle = TRIP_DETAIL_STYLE_MAP[bookingStatus ?? 'CONFIRMED'];
	if (!statusStyle) return null;
	const { text, textColor } = statusStyle;
	return (
		<div className="flex gap-20 items-start justify-center relative w-full">
			<div className="flex flex-col grow items-start justify-center">
				<Typography variant="body-xs" className="text-secondary">
					Booking ID:
				</Typography>
				<CopyToClipboard
					content={bookingId}
					title={bookingId}
					titleSize="body-sm"
					className="text-primary font-medium"
				/>
			</div>
			<div className="flex flex-col grow items-end justify-center">
				<Typography variant="body-xs" className="text-secondary">
					Status
				</Typography>
				<Skeleton
					loading={loading}
					variant="text"
					className="w-full rounded-5"
					level="body-xs"
					height={16}
					width={90}
				>
					<Typography
						variant="body-xs"
						fontWeight="medium"
						className={textColor}
					>
						{text}
					</Typography>
				</Skeleton>
			</div>
		</div>
	);
}

export function TripDetailContent(props: BookingSuccessResponse['data']) {
	const {
		bookingDetail,
		cancellationDetail,
		rescheduleDetail,
		refundDetail,
		heading,
		parentBookingId,
		rescheduleRefundDetail,
	} = props;
	const navigate = useNavigate();
	const { bookingId, bookingStatus } = bookingDetail;
	return (
		<div className="min-h-screen bg-tertiary flex flex-col main-content">
			<header className="sticky top-0 z-10 border-b border-tertiary">
				<NavigationHeader
					title={heading.title}
					subtitle={heading.subtitle}
					leftAction={() =>
						navigate({
							href: '/account/trips',
							reloadDocument: true,
							back: true,
							replace: true,
							viewTransition: {
								types: ['slide-right'],
							},
						})
					}
					rightIcon={<ShareIcon />}
					className="bg-primary"
				/>
			</header>
			<div className="flex flex-col flex-1 gap-y-10">
				<div className="p-20 bg-primary flex flex-col gap-y-10 items-start justify-start">
					<BookingHeader bookingId={bookingId} bookingStatus={bookingStatus} />
					<TripWidget
						bookingDetail={bookingDetail}
						cancellationDetail={cancellationDetail}
						rescheduleDetail={rescheduleDetail}
						refundDetail={refundDetail}
					/>
					<RescheduleRefundAlert
						rescheduleRefundDetail={rescheduleRefundDetail}
						parentBookingId={parentBookingId}
						className="mt-5"
					/>
				</div>
				<BookingDetailContent />
			</div>
		</div>
	);
}
